import React, { FC, ReactNode, useMemo } from 'react';
import { ImageActions, ImageItem } from './types';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { Button, Carousel, CarouselProps, Empty } from 'antd';
import type PhotoSwipe from 'photoswipe';
import type { UIElementData } from 'photoswipe';
import { LightBoxItem } from './ImagesLightBox';
import { getFormattedLabel } from '@/utils/helpers';

interface GalleryImagesProps extends CarouselProps {
  images: ImageItem[];
  className?: string;
  actions?: ImageActions;
  showCaption?: boolean;
}

type PhotoSwipeSlide = NonNullable<PhotoSwipe['currSlide']>;

const updateImageDimensions = (
  content: PhotoSwipeSlide['content'],
  slide: PhotoSwipeSlide,
) => {
  if (!(content.element instanceof HTMLImageElement)) return;

  const { naturalWidth, naturalHeight } = content.element;
  if (!naturalWidth || !naturalHeight) return;
  if (slide.width === naturalWidth && slide.height === naturalHeight) return;

  content.width = naturalWidth;
  content.height = naturalHeight;
  content.data.width = naturalWidth;
  content.data.height = naturalHeight;
  content.data.w = naturalWidth;
  content.data.h = naturalHeight;
  slide.width = naturalWidth;
  slide.height = naturalHeight;
  slide.resize();
};

const syncLoadedImageDimensions = (photoswipe: PhotoSwipe) => {
  photoswipe.on('loadComplete', ({ content, slide, isError }) => {
    if (!isError) updateImageDimensions(content, slide);
  });
  photoswipe.on('afterSetContent', ({ slide }) => {
    updateImageDimensions(slide.content, slide);
  });
};

export const GalleryImages: FC<GalleryImagesProps> = ({
  images,
  className,
  actions,
  showCaption,
  ...carouselProps
}) => (
  <div className={`custom-carousel${className ? ' ' + className : ''}`}>
    {images?.length ? (
      <Gallery onBeforeOpen={syncLoadedImageDimensions}>
        <Carousel arrows {...carouselProps}>
          {images.map((image, idx) => (
            <div key={idx} className="h-100">
              <div
                className="h-100 position-relative"
                style={
                  image.caption
                    ? { display: 'flex', flexDirection: 'column' }
                    : undefined
                }
              >
                <LightBoxItem
                  image={image}
                  actions={actions}
                  showNoImage
                  style={{
                    width: '100%',
                    height: image.caption ? undefined : '100%',
                    flex: image.caption ? '1 1 0' : undefined,
                    minHeight: 0,
                  }}
                />
                {image.caption ? (
                  <p
                    className="m-0 px-2 text-center"
                    style={{
                      fontSize: 12,
                      lineHeight: '18px',
                      flex: '0 0 auto',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {image.caption}
                  </p>
                ) : null}
                {showCaption ? (
                  <h4
                    className="text-center text-white position-absolute w-100 mb-0 px-2"
                    style={{ bottom: 8, left: 0, zIndex: 3 }}
                  >
                    {image.description || getFormattedLabel(image.key || '')}
                  </h4>
                ) : null}
              </div>
            </div>
          ))}
        </Carousel>
      </Gallery>
    ) : (
      <Empty description="No Images" />
    )}
  </div>
);

export const LightBoxLink: FC<{ image: ImageItem; children: ReactNode }> = ({
  image,
  children,
}) => {
  // Gallery's withCaption writes the caption as innerHTML; this keeps server text as plain text.
  const uiElements = useMemo<UIElementData[]>(
    () => [
      {
        name: 'text-caption',
        order: 9,
        isButton: false,
        appendTo: 'root',
        onInit: (element) => {
          Object.assign(element.style, {
            position: 'absolute',
            bottom: '15px',
            left: '0',
            right: '0',
            padding: '0 20px',
            color: 'var(--pswp-icon-color)',
            textAlign: 'center',
            fontSize: '14px',
            textShadow: '1px 1px 3px var(--pswp-icon-color-secondary)',
          });
          element.textContent = image.caption ?? '';
        },
      },
    ],
    [image.caption],
  );
  return (
    <Gallery
      onBeforeOpen={syncLoadedImageDimensions}
      uiElements={uiElements}
      options={{ showHideAnimationType: 'fade' }}
    >
      <Item
        original={image.imageFileUrl}
        thumbnail={image.thumbnailImageUrl}
        width={1600}
        height={1200}
        caption={image.caption}
      >
        {({ ref, open }) => (
          <Button
            ref={ref}
            type="link"
            size="small"
            className="p-0 h-auto"
            style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={open}
          >
            {children}
          </Button>
        )}
      </Item>
    </Gallery>
  );
};
